import React, { useState, useEffect } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Lock, Mail, Download, Users, ArrowLeft, Search, Radio, Save, Video, Upload, Plus, Trash2, BarChart3, Loader2, LogOut, Shield, UserCog, Instagram, GripVertical, Image as ImageIcon, Send, CalendarClock, KeyRound, Eye, X, Flag, Newspaper, Pencil } from "lucide-react";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { useToast } from "../hooks/use-toast";
import { club, playingLevels, heroImages } from "../mock";
import HeroVideoLoop from "../components/HeroVideoLoop";
import HeroPlaylist from "../components/HeroPlaylist";
import { extractYouTubeId } from "../lib/video";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = "asf_token";
// Original stock images used in each public section — "Use Default Photo" restores these.
const DEFAULT_SECTION_IMAGE = {
  hero: heroImages.main,
  registration: heroImages.celebrate,
};
const DAYS = [
  { v: "mon", l: "Monday" }, { v: "tue", l: "Tuesday" }, { v: "wed", l: "Wednesday" },
  { v: "thu", l: "Thursday" }, { v: "fri", l: "Friday" }, { v: "sat", l: "Saturday" }, { v: "sun", l: "Sunday" },
];

const STATUSES = ["New", "Invited", "Waitlisted", "Declined"];
const statusClass = {
  New: "text-white/60 border-white/20",
  Invited: "text-gold border-gold/40",
  Waitlisted: "text-amber-400 border-amber-400/40",
  Declined: "text-red-400 border-red-400/40",
};

const secToClock = (s) => {
  const n = Math.max(0, parseInt(s || 0, 10));
  const h = Math.floor(n / 3600);
  const m = Math.floor((n % 3600) / 60);
  const sec = n % 60;
  const mm = h > 0 ? String(m).padStart(2, "0") : String(m);
  return (h > 0 ? `${h}:` : "") + `${mm}:${String(sec).padStart(2, "0")}`;
};
const clockToSec = (str) => {
  const parts = String(str).split(":").map((p) => parseInt(p, 10) || 0);
  if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
  if (parts.length === 2) return parts[0] * 60 + parts[1];
  return parts[0] || 0;
};

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [forgot, setForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotSent, setForgotSent] = useState(false);

  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [levelFilter, setLevelFilter] = useState("all");
  const [tab, setTab] = useState("media");
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [stats, setStats] = useState(null);
  const [uploading, setUploading] = useState({ hero: false, registration: false });
  const [users, setUsers] = useState([]);
  const [newUser, setNewUser] = useState({ email: "", name: "", password: "", role: "user" });
  // Seasons feature state
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [seasonFilter, setSeasonFilter] = useState("none");
  const [bulkSeason, setBulkSeason] = useState("");
  const [newSeason, setNewSeason] = useState("");
  const [renaming, setRenaming] = useState({ old: "", val: "" });
  const [editRow, setEditRow] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [editPhotoUploading, setEditPhotoUploading] = useState(false);
  const { toast } = useToast();

  const authHeaders = () => ({ Authorization: `Bearer ${token}` });
  const isAdmin = user?.role === "admin";

  const loadRows = async () => {
    try { const res = await axios.get(`${API}/registrations`, { headers: authHeaders() }); setRows(res.data); } catch (e) { console.error("Failed to load registrations", e); setRows([]); }
  };
  const loadSettings = async () => {
    try { const res = await axios.get(`${API}/settings`); setSettings(res.data); } catch (e) { console.error("Failed to load settings", e); }
  };
  const loadStats = async () => {
    try { const res = await axios.get(`${API}/admin/stats`, { headers: authHeaders() }); setStats(res.data); } catch (e) { console.error("Failed to load stats", e); }
  };
  const loadUsers = async () => {
    try { const res = await axios.get(`${API}/auth/users`, { headers: authHeaders() }); setUsers(res.data); } catch (e) { console.error("Failed to load users", e); }
  };

  // Verify existing session on mount
  useEffect(() => {
    let active = true;
    const check = async () => {
      if (!token) { setChecking(false); return; }
      try {
        const res = await axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } });
        if (active) setUser(res.data);
      } catch (e) {
        localStorage.removeItem(TOKEN_KEY);
        if (active) { setToken(""); setUser(null); }
      } finally {
        if (active) setChecking(false);
      }
    };
    check();
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load data after user is known
  useEffect(() => {
    if (!user) return;
    loadSettings();
    setTab(user.role === "admin" ? "contacts" : "media");
    if (user.role === "admin") { loadRows(); loadStats(); loadUsers(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const login = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const res = await axios.post(`${API}/auth/login`, loginForm);
      localStorage.setItem(TOKEN_KEY, res.data.token);
      setToken(res.data.token);
      setUser(res.data.user);
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError(typeof d === "string" ? d : "Incorrect email or password");
    }
  };

  const submitForgot = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: forgotEmail });
    } catch (err) { console.error("Forgot-password request failed", err); /* UI shows generic response either way */ }
    setForgotSent(true);
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(""); setUser(null); setRows([]); setStats(null); setUsers([]);
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/settings`, settings, { headers: authHeaders() });
      setSettings(res.data);
      toast({ title: "Saved", description: "Site video & live settings updated." });
    } catch (err) {
      toast({ title: "Save failed", description: "Please try again." });
    } finally { setSaving(false); }
  };

  const uploadVideo = async (which, file) => {
    if (!file) return;
    setUploading((u) => ({ ...u, [which]: true }));
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/admin/upload-video`, fd, {
        headers: { ...authHeaders(), "Content-Type": "multipart/form-data" },
      });
      const url = `${BACKEND}${res.data.url}`;
      if (which === "hero") setSettings((s) => ({ ...s, heroVideoType: "mp4", heroVideoId: url }));
      else setSettings((s) => ({ ...s, registrationVideoType: "mp4", registrationVideoId: url }));
      toast({ title: "Uploaded", description: "Video uploaded. Remember to Save Changes." });
    } catch (err) {
      toast({ title: "Upload failed", description: "Check the file and try again." });
    } finally {
      setUploading((u) => ({ ...u, [which]: false }));
    }
  };

  const uploadSectionImage = async (which, file) => {
    if (!file) return;
    setUploading((u) => ({ ...u, [which]: true }));
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/admin/upload-image`, fd, {
        headers: { ...authHeaders(), "Content-Type": "multipart/form-data" },
      });
      const url = `${BACKEND}${res.data.url}`;
      setSettings((s) => ({ ...s, [`${which}VideoType`]: "image", [`${which}VideoId`]: url }));
      toast({ title: "Image uploaded", description: "Remember to Save Changes." });
    } catch (err) {
      toast({ title: "Upload failed", description: "Try a JPG/PNG under a few MB." });
    } finally {
      setUploading((u) => ({ ...u, [which]: false }));
    }
  };

  const applyDefaultImage = (which) =>
    setSettings((s) => ({ ...s, [`${which}VideoType`]: "image", [`${which}VideoId`]: DEFAULT_SECTION_IMAGE[which] }));

  const updateStatus = async (regId, status) => {
    setRows((rs) => rs.map((r) => (r.id === regId ? { ...r, status } : r)));
    try {
      await axios.patch(`${API}/registrations/${regId}/status`, { status }, { headers: authHeaders() });
    } catch (e) {
      toast({ title: "Status update failed", description: "Please try again." });
      loadRows();
    }
  };

  const updateNotes = async (regId, notes) => {
    try {
      await axios.patch(`${API}/registrations/${regId}/notes`, { notes }, { headers: authHeaders() });
    } catch (e) {
      toast({ title: "Note save failed", description: "Please try again." });
      loadRows();
    }
  };

  const deleteRegistration = async (regId) => {
    if (!window.confirm("Delete this registration permanently? This cannot be undone.")) return;
    setRows((rs) => rs.filter((r) => r.id !== regId));
    try {
      await axios.delete(`${API}/registrations/${regId}`, { headers: authHeaders() });
      loadStats();
      toast({ title: "Deleted", description: "Registration removed." });
    } catch (e) {
      toast({ title: "Delete failed", description: "Please try again." });
      loadRows();
    }
  };

  const [emailingRoster, setEmailingRoster] = useState(false);
  const emailRoster = async () => {
    setEmailingRoster(true);
    try {
      const res = await axios.post(`${API}/admin/email-roster`, { q, dateFrom, dateTo, level: levelFilter }, { headers: authHeaders() });
      toast({ title: "List emailed", description: `Sent ${res.data.count} player(s) to ${res.data.to}.` });
    } catch (e) {
      toast({ title: "Email failed", description: "Check SMTP settings and try again." });
    } finally {
      setEmailingRoster(false);
    }
  };

  const createUser = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/auth/users`, newUser, { headers: authHeaders() });
      setNewUser({ email: "", name: "", password: "", role: "user" });
      loadUsers();
      toast({ title: "User added", description: `${newUser.email} can now log in.` });
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast({ title: "Could not add user", description: typeof d === "string" ? d : "Please try again." });
    }
  };

  const deleteUser = async (id) => {
    try {
      await axios.delete(`${API}/auth/users/${id}`, { headers: authHeaders() });
      loadUsers();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast({ title: "Could not delete user", description: typeof d === "string" ? d : "Please try again." });
    }
  };

  const setLive = (k, v) => setSettings((s) => ({ ...s, live: { ...s.live, [k]: v } }));
  const setS = (k, v) => setSettings((s) => ({ ...s, [k]: v }));

  const [feedUploading, setFeedUploading] = useState(-1);
  const [dragIndex, setDragIndex] = useState(-1);
  const addFeedItem = () => setSettings((s) => ({ ...s, followFeed: [...(s.followFeed || []), { image: "", caption: "", link: "", isReel: false }] }));
  const updateFeedItem = (i, k, v) => setSettings((s) => ({ ...s, followFeed: (s.followFeed || []).map((it, idx) => (idx === i ? { ...it, [k]: v } : it)) }));
  const removeFeedItem = (i) => setSettings((s) => ({ ...s, followFeed: (s.followFeed || []).filter((_, idx) => idx !== i) }));
  const moveFeedItem = (from, to) => {
    setSettings((s) => {
      const arr = [...(s.followFeed || [])];
      if (from < 0 || to < 0 || from >= arr.length || to >= arr.length) return s;
      const [moved] = arr.splice(from, 1);
      arr.splice(to, 0, moved);
      return { ...s, followFeed: arr };
    });
  };
  const uploadFeedImage = async (i, file) => {
    if (!file) return;
    setFeedUploading(i);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/admin/upload-image`, fd, { headers: { ...authHeaders(), "Content-Type": "multipart/form-data" } });
      updateFeedItem(i, "image", `${BACKEND}${res.data.url}`);
      toast({ title: "Image uploaded", description: "Remember to Save Changes." });
    } catch (e) {
      toast({ title: "Upload failed", description: "Try a JPG/PNG under a few MB." });
    } finally {
      setFeedUploading(-1);
    }
  };

  // Club Photos (Gallery) manager
  const [galleryUploading, setGalleryUploading] = useState(-1);
  const [galleryDrag, setGalleryDrag] = useState(-1);
  const addGalleryItem = () => setSettings((s) => ({ ...s, galleryItems: [...(s.galleryItems || []), { image: "", caption: "" }] }));
  const updateGalleryItem = (i, k, v) => setSettings((s) => ({ ...s, galleryItems: (s.galleryItems || []).map((it, idx) => (idx === i ? { ...it, [k]: v } : it)) }));
  const removeGalleryItem = (i) => setSettings((s) => ({ ...s, galleryItems: (s.galleryItems || []).filter((_, idx) => idx !== i) }));
  const moveGalleryItem = (from, to) => setSettings((s) => {
    const arr = [...(s.galleryItems || [])];
    if (from < 0 || to < 0 || from >= arr.length || to >= arr.length) return s;
    const [m] = arr.splice(from, 1); arr.splice(to, 0, m);
    return { ...s, galleryItems: arr };
  });
  const uploadGalleryImage = async (i, file) => {
    if (!file) return;
    setGalleryUploading(i);
    try {
      const fd = new FormData(); fd.append("file", file);
      const res = await axios.post(`${API}/admin/upload-image`, fd, { headers: { ...authHeaders(), "Content-Type": "multipart/form-data" } });
      updateGalleryItem(i, "image", `${BACKEND}${res.data.url}`);
      toast({ title: "Image uploaded", description: "Remember to Save Changes." });
    } catch (e) {
      console.error("Gallery image upload failed", e);
      toast({ title: "Upload failed", description: "Try a JPG/PNG under a few MB." });
    } finally { setGalleryUploading(-1); }
  };

  // Latest News manager
  const [newsUploading, setNewsUploading] = useState(-1);
  const [newsDrag, setNewsDrag] = useState(-1);
  const addNewsItem = () => setSettings((s) => ({ ...s, newsItems: [...(s.newsItems || []), { date: "", category: "", title: "", excerpt: "", image: "" }] }));
  const updateNewsItem = (i, k, v) => setSettings((s) => ({ ...s, newsItems: (s.newsItems || []).map((it, idx) => (idx === i ? { ...it, [k]: v } : it)) }));
  const removeNewsItem = (i) => setSettings((s) => ({ ...s, newsItems: (s.newsItems || []).filter((_, idx) => idx !== i) }));
  const moveNewsItem = (from, to) => setSettings((s) => {
    const arr = [...(s.newsItems || [])];
    if (from < 0 || to < 0 || from >= arr.length || to >= arr.length) return s;
    const [m] = arr.splice(from, 1); arr.splice(to, 0, m);
    return { ...s, newsItems: arr };
  });
  const uploadNewsImage = async (i, file) => {
    if (!file) return;
    setNewsUploading(i);
    try {
      const fd = new FormData(); fd.append("file", file);
      const res = await axios.post(`${API}/admin/upload-image`, fd, { headers: { ...authHeaders(), "Content-Type": "multipart/form-data" } });
      updateNewsItem(i, "image", `${BACKEND}${res.data.url}`);
      toast({ title: "Image uploaded", description: "Remember to Save Changes." });
    } catch (e) {
      console.error("News image upload failed", e);
      toast({ title: "Upload failed", description: "Try a JPG/PNG under a few MB." });
    } finally { setNewsUploading(-1); }
  };


  // Hero video playlist (multiple background clips that cycle on the header)
  const [heroClipUploading, setHeroClipUploading] = useState(-1);
  const [heroDragIndex, setHeroDragIndex] = useState(-1);
  const addHeroClip = () => setSettings((s) => ({ ...s, heroPlaylist: [...(s.heroPlaylist || []), { type: "youtube", source: "" }] }));
  const updateHeroClip = (i, k, v) => setSettings((s) => ({ ...s, heroPlaylist: (s.heroPlaylist || []).map((c, idx) => (idx === i ? { ...c, [k]: v } : c)) }));
  const removeHeroClip = (i) => setSettings((s) => ({ ...s, heroPlaylist: (s.heroPlaylist || []).filter((_, idx) => idx !== i) }));
  const moveHeroClip = (from, to) => {
    setSettings((s) => {
      const arr = [...(s.heroPlaylist || [])];
      if (from < 0 || to < 0 || from >= arr.length || to >= arr.length) return s;
      const [moved] = arr.splice(from, 1);
      arr.splice(to, 0, moved);
      return { ...s, heroPlaylist: arr };
    });
  };
  const uploadHeroClip = async (i, file) => {
    if (!file) return;
    setHeroClipUploading(i);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/admin/upload-video`, fd, { headers: { ...authHeaders(), "Content-Type": "multipart/form-data" } });
      const url = `${BACKEND}${res.data.url}`;
      setSettings((s) => ({ ...s, heroPlaylist: (s.heroPlaylist || []).map((c, idx) => (idx === i ? { ...c, source: url, type: "mp4" } : c)) }));
      toast({ title: "Uploaded", description: "Clip uploaded. Remember to Save Changes." });
    } catch (e) {
      toast({ title: "Upload failed", description: "Check the file and try again." });
    } finally {
      setHeroClipUploading(-1);
    }
  };
  const addHeroClipLoop = (i) => setSettings((s) => ({ ...s, heroPlaylist: (s.heroPlaylist || []).map((c, idx) => (idx === i ? { ...c, loops: [...(c.loops || []), { start: 0, end: 0 }] } : c)) }));
  const updateHeroClipLoop = (i, li, key, clock) => setSettings((s) => ({ ...s, heroPlaylist: (s.heroPlaylist || []).map((c, idx) => (idx === i ? { ...c, loops: (c.loops || []).map((l, x) => (x === li ? { ...l, [key]: clockToSec(clock) } : l)) } : c)) }));
  const removeHeroClipLoop = (i, li) => setSettings((s) => ({ ...s, heroPlaylist: (s.heroPlaylist || []).map((c, idx) => (idx === i ? { ...c, loops: (c.loops || []).filter((_, x) => x !== li) } : c)) }));
  const [loopDrag, setLoopDrag] = useState({ clip: -1, idx: -1 });
  const moveHeroClipLoop = (i, from, to) => setSettings((s) => ({
    ...s,
    heroPlaylist: (s.heroPlaylist || []).map((c, idx) => {
      if (idx !== i) return c;
      const arr = [...(c.loops || [])];
      if (from < 0 || to < 0 || from >= arr.length || to >= arr.length) return c;
      const [moved] = arr.splice(from, 1);
      arr.splice(to, 0, moved);
      return { ...c, loops: arr };
    }),
  }));

  // Match days + playlist preview mode
  const [newMatchDay, setNewMatchDay] = useState("");
  const [previewMode, setPreviewMode] = useState("normal");
  const addMatchDay = () => {
    if (!newMatchDay) return;
    setSettings((s) => ({ ...s, matchDays: Array.from(new Set([...(s.matchDays || []), newMatchDay])) }));
    setNewMatchDay("");
  };
  const removeMatchDay = (d) => setSettings((s) => ({ ...s, matchDays: (s.matchDays || []).filter((x) => x !== d) }));
  // Fixtures — each fixture date automatically counts as a match day.
  const addFixture = () => setSettings((s) => ({ ...s, fixtures: [...(s.fixtures || []), { date: "", opponent: "", home: true, time: "" }] }));
  const updateFixture = (i, k, v) => setSettings((s) => ({ ...s, fixtures: (s.fixtures || []).map((f, idx) => (idx === i ? { ...f, [k]: v } : f)) }));
  const removeFixture = (i) => setSettings((s) => ({ ...s, fixtures: (s.fixtures || []).filter((_, idx) => idx !== i) }));
  // Debounced so typing a source/loop doesn't remount the preview's video player on every keystroke.
  const [previewClips, setPreviewClips] = useState([]);
  useEffect(() => {
    const filt = (settings?.heroPlaylist || []).filter((c) => {
      const s = c.schedule || "always";
      return s === "always" || (previewMode === "match" ? s === "match" : s === "normal");
    });
    const t = setTimeout(() => setPreviewClips(filt), 600);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(settings?.heroPlaylist || []), previewMode]);

  // Change password
  const [showChangePw, setShowChangePw] = useState(false);
  const [pwForm, setPwForm] = useState({ current: "", next: "", confirm: "" });
  const [pwSaving, setPwSaving] = useState(false);
  const changePassword = async (e) => {
    e.preventDefault();
    if (pwForm.next.length < 6) { toast({ title: "Too short", description: "New password must be at least 6 characters." }); return; }
    if (pwForm.next !== pwForm.confirm) { toast({ title: "Passwords don't match" }); return; }
    setPwSaving(true);
    try {
      await axios.post(`${API}/auth/change-password`, { current_password: pwForm.current, new_password: pwForm.next }, { headers: authHeaders() });
      toast({ title: "Password updated", description: "Use your new password next time you log in." });
      setShowChangePw(false);
      setPwForm({ current: "", next: "", confirm: "" });
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast({ title: "Could not update", description: typeof d === "string" ? d : "Please try again." });
    } finally { setPwSaving(false); }
  };

  // Weekly digest preview
  const [digest, setDigest] = useState(null);
  const [loadingDigest, setLoadingDigest] = useState(false);
  const previewDigest = async () => {
    setLoadingDigest(true);
    try {
      const res = await axios.get(`${API}/admin/reminder-preview`, { headers: authHeaders() });
      setDigest(res.data);
    } catch (e) {
      toast({ title: "Preview failed", description: "Please try again." });
    } finally { setLoadingDigest(false); }
  };

  const seasons = settings?.seasons || [];
  const applySeasonsResp = (data) => setSettings((s) => ({ ...(s || {}), seasons: data.seasons || [] }));
  const setTeamsNameDisplay = async (mode) => {
    const next = { ...(settings || {}), teamsNameDisplay: mode };
    setSettings(next);
    try {
      await axios.put(`${API}/settings`, next, { headers: authHeaders() });
      toast({ title: "Saved", description: "Public Teams name display updated." });
    } catch (e) { console.error("Save name display failed", e); toast({ title: "Save failed", description: "Try again." }); }
  };
  const addSeason = async () => {
    const name = newSeason.trim();
    if (!name) return;
    try {
      const res = await axios.post(`${API}/seasons`, { name }, { headers: authHeaders() });
      applySeasonsResp(res.data); setNewSeason(""); toast({ title: "Season added", description: name });
    } catch (e) { toast({ title: "Could not add season", description: e?.response?.data?.detail || "Try again." }); }
  };
  const commitRename = async (oldName, val) => {
    const nn = (val || "").trim();
    if (!nn || nn === oldName) { setRenaming({ old: "", val: "" }); return; }
    try {
      const res = await axios.put(`${API}/seasons`, { old: oldName, new: nn }, { headers: authHeaders() });
      applySeasonsResp(res.data); setRenaming({ old: "", val: "" }); loadRows();
      if (seasonFilter === oldName) setSeasonFilter(nn);
      toast({ title: "Season renamed" });
    } catch (e) { toast({ title: "Rename failed", description: e?.response?.data?.detail || "Try again." }); }
  };
  const deleteSeason = async (name) => {
    try {
      const res = await axios.delete(`${API}/seasons/${encodeURIComponent(name)}`, { headers: authHeaders() });
      applySeasonsResp(res.data); loadRows();
      if (seasonFilter === name) setSeasonFilter("none");
      if (bulkSeason === name) setBulkSeason("");
      toast({ title: "Season deleted", description: name });
    } catch (e) { toast({ title: "Delete failed", description: "Try again." }); }
  };
  const toggleSelect = (id) => setSelectedIds((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleSelectAll = (ids, checked) => setSelectedIds((prev) => { const n = new Set(prev); ids.forEach((id) => (checked ? n.add(id) : n.delete(id))); return n; });
  const bulkAssign = async (action) => {
    if (!bulkSeason) { toast({ title: "Pick a season first" }); return; }
    const ids = Array.from(selectedIds);
    if (!ids.length) { toast({ title: "Select players first" }); return; }
    try {
      await axios.post(`${API}/registrations/bulk-seasons`, { ids, season: bulkSeason, action }, { headers: authHeaders() });
      await loadRows(); setSelectedIds(new Set());
      toast({ title: action === "add" ? "Added to season" : "Removed from season", description: `${ids.length} player(s) · ${bulkSeason}` });
    } catch (e) { toast({ title: "Action failed", description: "Try again." }); }
  };
  const toggleEditSeason = (name) => setEditRow((r) => { const cur = new Set(r.seasons || []); cur.has(name) ? cur.delete(name) : cur.add(name); return { ...r, seasons: Array.from(cur) }; });
  const saveEdit = async () => {
    if (!editRow) return;
    setSavingEdit(true);
    try {
      const f = editRow;
      const payload = {
        playerFirstName: f.playerFirstName, playerLastName: f.playerLastName, dob: f.dob,
        playingLevel: f.playingLevel, position: f.position, guardianName: f.guardianName,
        email: f.email, phone: f.phone, emergencyContact: f.emergencyContact, emergencyPhone: f.emergencyPhone,
        medicalNotes: f.medicalNotes, notes: f.notes, status: f.status, seasons: f.seasons || [], photoUrl: f.photoUrl || "",
      };
      const res = await axios.put(`${API}/registrations/${f.id}`, payload, { headers: authHeaders() });
      setRows((rs) => rs.map((r) => (r.id === f.id ? res.data : r)));
      setEditRow(null); toast({ title: "Player updated" });
    } catch (e) { toast({ title: "Update failed", description: e?.response?.data?.detail || "Check the fields and try again." }); }
    finally { setSavingEdit(false); }
  };
  const uploadEditPhoto = async (file) => {
    if (!file || !editRow) return;
    setEditPhotoUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const res = await axios.post(`${API}/admin/upload-image`, fd, { headers: { ...authHeaders(), "Content-Type": "multipart/form-data" } });
      setEditRow((r) => ({ ...r, photoUrl: `${BACKEND}${res.data.url}` }));
      toast({ title: "Photo uploaded", description: "Click Save Player to keep it." });
    } catch (e) { console.error("Photo upload failed", e); toast({ title: "Upload failed", description: "Try a JPG/PNG." }); }
    finally { setEditPhotoUploading(false); }
  };

  const filtered = rows.filter((r) => {
    const s = `${r.playerFirstName} ${r.playerLastName} ${r.email} ${r.guardianName} ${r.playingLevel}`.toLowerCase();
    if (!s.includes(q.toLowerCase())) return false;
    if (levelFilter !== "all" && (r.playingLevel || "") !== levelFilter) return false;
    if (seasonFilter === "none") { if ((r.seasons || []).length !== 0) return false; }
    else if (seasonFilter !== "all" && !(r.seasons || []).includes(seasonFilter)) return false;
    const dt = r.submittedAt ? new Date(r.submittedAt) : null;
    if (dateFrom && dt && dt < new Date(dateFrom + "T00:00:00")) return false;
    if (dateTo && dt && dt > new Date(dateTo + "T23:59:59")) return false;
    return true;
  });

  const exportCsv = () => {
    const headers = ["Player", "DOB", "Level", "Position", "Guardian", "Email", "Phone", "Emergency", "Emergency Phone", "Status", "Seasons", "Notes", "Signature", "Submitted"];
    const lines = filtered.map((r) => [
      `${r.playerFirstName} ${r.playerLastName}`, r.dob, r.playingLevel, r.position, r.guardianName,
      r.email, r.phone, r.emergencyContact, r.emergencyPhone, r.status || "New", (r.seasons || []).join("; "), r.notes || "", r.signature, r.submittedAt,
    ].map((v) => `"${(v || "").toString().replace(/"/g, '""')}"`).join(","));
    const csv = [headers.join(","), ...lines].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const slug = seasonFilter === "all" ? "all" : seasonFilter === "none" ? "unassigned" : seasonFilter.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
    a.href = url; a.download = `atletico-roster-${slug}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  // ---------- Login screen ----------
  if (checking) {
    return <div className="min-h-screen bg-background flex items-center justify-center text-white/50"><Loader2 className="animate-spin" /></div>;
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-5">
        {forgot ? (
          <div className="w-full max-w-sm bg-card border border-white/10 rounded-xl p-8" data-testid="forgot-panel">
            <img src={club.crest} alt="crest" className="h-20 w-20 object-contain mx-auto" />
            <h1 className="display-font uppercase text-white text-2xl text-center mt-4">Reset Password</h1>
            {forgotSent ? (
              <p className="text-white/70 font-light text-center mt-6" data-testid="forgot-sent">
                If that email is registered, a reset link is on its way. Check your inbox.
              </p>
            ) : (
              <form onSubmit={submitForgot} className="mt-6" data-testid="forgot-form">
                <p className="text-white/50 text-sm font-light text-center mb-4">Enter your email and we'll send a reset link.</p>
                <div className="relative">
                  <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                  <Input type="email" required value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} placeholder="Email" className="bg-background border-white/15 pl-9" data-testid="forgot-email" />
                </div>
                <button type="submit" className="w-full mt-4 bg-gold text-black cond-font uppercase tracking-widest font-semibold py-3 rounded-sm hover:bg-gold-light transition-colors" data-testid="forgot-submit">
                  Send Reset Link
                </button>
              </form>
            )}
            <button onClick={() => { setForgot(false); setForgotSent(false); }} className="block w-full text-center text-white/50 hover:text-gold text-sm mt-5 transition-colors" data-testid="forgot-back">
              Back to login
            </button>
          </div>
        ) : (
          <form onSubmit={login} className="w-full max-w-sm bg-card border border-white/10 rounded-xl p-8" data-testid="login-form">
            <img src={club.crest} alt="crest" className="h-20 w-20 object-contain mx-auto" />
            <h1 className="display-font uppercase text-white text-2xl text-center mt-4">Staff Login</h1>
            <p className="text-white/50 text-sm text-center font-light mt-1">Roster · Live · Video</p>
            <div className="relative mt-6">
              <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
              <Input type="email" value={loginForm.email} onChange={(e) => setLoginForm((f) => ({ ...f, email: e.target.value }))} placeholder="Email" className="bg-background border-white/15 pl-9" data-testid="login-email" />
            </div>
            <div className="relative mt-3">
              <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
              <Input type="password" value={loginForm.password} onChange={(e) => setLoginForm((f) => ({ ...f, password: e.target.value }))} placeholder="Password" className="bg-background border-white/15 pl-9" data-testid="login-password" />
            </div>
            {error && <p className="text-red-400 text-sm mt-2" data-testid="login-error">{error}</p>}
            <button type="submit" className="w-full mt-5 bg-gold text-black cond-font uppercase tracking-widest font-semibold py-3 rounded-sm hover:bg-gold-light transition-colors" data-testid="login-submit">
              Sign In
            </button>
            <button type="button" onClick={() => setForgot(true)} className="block w-full text-center text-white/50 hover:text-gold text-sm mt-4 transition-colors" data-testid="forgot-link">
              Forgot password?
            </button>
            <Link to="/" className="flex items-center justify-center gap-1 text-white/40 hover:text-gold text-sm mt-3 transition-colors">
              <ArrowLeft size={14} /> Back to site
            </Link>
          </form>
        )}
      </div>
    );
  }

  const tabs = isAdmin
    ? [
        { id: "contacts", label: "Roster", icon: Users },
        { id: "media", label: "Live & Video", icon: Radio },
        { id: "users", label: "Users", icon: UserCog },
      ]
    : [{ id: "media", label: "Live & Video", icon: Radio }];

  return (
    <div className="min-h-screen bg-background">
      <div className="border-b border-white/10 bg-card">
        <div className="max-w-7xl mx-auto px-5 py-5 flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <img src={club.crest} alt="crest" className="h-12 w-12 object-contain" />
            <div>
              <h1 className="display-font uppercase text-white text-xl leading-none">Club Admin</h1>
              <p className="cond-font uppercase tracking-widest text-[10px] text-gold">{user.email} · {user.role}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/" className="cond-font uppercase tracking-widest text-xs text-white/60 hover:text-gold transition-colors">← Site</Link>
            {tab === "contacts" && isAdmin && (
              <button onClick={exportCsv} className="inline-flex items-center gap-2 bg-gold text-black cond-font uppercase tracking-widest text-xs font-semibold px-4 py-2.5 rounded-sm hover:bg-gold-light transition-colors" data-testid="export-csv-btn">
                <Download size={15} /> Export CSV{seasonFilter === "all" ? "" : seasonFilter === "none" ? " · Unassigned" : ` · ${seasonFilter}`}
              </button>
            )}
            {tab === "contacts" && isAdmin && (
              <button onClick={emailRoster} disabled={emailingRoster} className="inline-flex items-center gap-2 border border-white/25 text-white cond-font uppercase tracking-widest text-xs px-4 py-2.5 rounded-sm hover:border-gold hover:text-gold transition-colors disabled:opacity-60" data-testid="email-roster-btn">
                {emailingRoster ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />} Email List
              </button>
            )}
            <button onClick={() => setShowChangePw(true)} className="inline-flex items-center gap-2 border border-white/20 text-white/70 cond-font uppercase tracking-widest text-xs px-4 py-2.5 rounded-sm hover:border-gold hover:text-gold transition-colors" data-testid="change-pw-btn">
              <KeyRound size={15} /> Password
            </button>
            <button onClick={logout} className="inline-flex items-center gap-2 border border-white/20 text-white/70 cond-font uppercase tracking-widest text-xs px-4 py-2.5 rounded-sm hover:border-gold hover:text-gold transition-colors" data-testid="logout-btn">
              <LogOut size={15} /> Logout
            </button>
          </div>
        </div>
        {/* Tabs */}
        <div className="max-w-7xl mx-auto px-5 flex gap-1">
          {tabs.map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`inline-flex items-center gap-2 cond-font uppercase tracking-widest text-xs px-4 py-3 border-b-2 transition-colors ${
                  tab === t.id ? "border-gold text-gold" : "border-transparent text-white/50 hover:text-white"
                }`}
                data-testid={`tab-${t.id}`}
              >
                <Icon size={15} /> {t.label}
              </button>
            );
          })}
        </div>
      </div>

      {tab === "contacts" && isAdmin && (
        <div className="max-w-7xl mx-auto px-5 py-8">
          {/* Analytics */}
          <div className="grid sm:grid-cols-3 gap-4 mb-8">
            <div className="bg-card border border-white/10 rounded-xl p-6" data-testid="stat-total">
              <div className="cond-font uppercase tracking-widest text-[10px] text-white/50">Total Registrations</div>
              <div className="display-font text-4xl text-gold mt-2">{stats ? stats.total : rows.length}</div>
            </div>
            <div className="bg-card border border-white/10 rounded-xl p-6" data-testid="stat-last7">
              <div className="cond-font uppercase tracking-widest text-[10px] text-white/50">Signups · Last 7 Days</div>
              <div className="display-font text-4xl text-gold mt-2">{stats ? stats.last7 : "—"}</div>
            </div>
            <div className="bg-card border border-white/10 rounded-xl p-6 sm:col-span-1">
              <div className="cond-font uppercase tracking-widest text-[10px] text-white/50 flex items-center gap-2">
                <BarChart3 size={13} className="text-gold" /> Weekly Trend
              </div>
              <div className="flex items-end gap-1.5 h-16 mt-3" data-testid="stat-trend">
                {(stats?.trend || []).map((b, i) => {
                  const max = Math.max(1, ...(stats?.trend || []).map((x) => x.count));
                  return (
                    <div key={i} className="flex-1 flex flex-col items-center justify-end h-full" title={`${b.label}: ${b.count}`}>
                      <div
                        className="w-full bg-gold/80 rounded-t-sm transition-all"
                        style={{ height: `${(b.count / max) * 100}%`, minHeight: b.count > 0 ? "4px" : "0px" }}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Seasons manager */}
          <div className="bg-card border border-white/10 rounded-xl p-6 mb-6" data-testid="seasons-manager">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-md bg-gold/15 border border-gold/30 flex items-center justify-center text-gold"><CalendarClock size={18} /></div>
              <div>
                <h3 className="cond-font uppercase tracking-wide text-white leading-none">Seasons</h3>
                <p className="text-white/40 text-xs font-light mt-1">Create seasons, then select players below and add them. Players can be in multiple seasons.</p>
              </div>
            </div>
            <div className="flex items-center gap-2 mb-4 max-w-md">
              <Input value={newSeason} onChange={(e) => setNewSeason(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addSeason()} placeholder="New season (e.g. Spring 2026)" className="bg-background border-white/15 h-10" data-testid="season-new-input" />
              <button onClick={addSeason} className="inline-flex items-center gap-2 bg-gold text-black cond-font uppercase tracking-widest text-xs font-semibold px-4 py-2.5 rounded-sm hover:bg-gold-light transition-colors shrink-0" data-testid="season-add-btn"><Plus size={15} /> Add</button>
            </div>
            <div className="flex items-center gap-3 mb-4 flex-wrap">
              <Label className="cond-font uppercase tracking-wide text-[10px] text-white/50">Public Teams page shows names as</Label>
              <div className="w-56">
                <Select value={settings?.teamsNameDisplay || "initial"} onValueChange={setTeamsNameDisplay}>
                  <SelectTrigger className="bg-background border-white/15 h-9" data-testid="teams-name-display"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="full">Full name (Alex Rivera)</SelectItem>
                    <SelectItem value="initial">First name + initial (Alex R.)</SelectItem>
                    <SelectItem value="first">First name only (Alex)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {seasons.length === 0 ? (
              <p className="text-white/40 text-sm font-light">No seasons yet. Add one above to start grouping players.</p>
            ) : (
              <div className="flex flex-wrap items-center gap-2" data-testid="seasons-list">
                <button onClick={() => setSeasonFilter("none")} className={`text-sm cond-font tracking-wide rounded-full px-3 py-1.5 border transition-colors ${seasonFilter === "none" ? "bg-gold text-black border-gold" : "text-white/70 border-white/15 hover:border-gold"}`} data-testid="season-chip-none">No group <span className="opacity-60 text-xs">{rows.filter((r) => (r.seasons || []).length === 0).length}</span></button>
                <button onClick={() => setSeasonFilter("all")} className={`text-sm cond-font tracking-wide rounded-full px-3 py-1.5 border transition-colors ${seasonFilter === "all" ? "bg-gold text-black border-gold" : "text-white/70 border-white/15 hover:border-gold"}`} data-testid="season-chip-all">All players <span className="opacity-60 text-xs">{rows.length}</span></button>
                <span className="w-px h-6 bg-white/10 mx-1" />
                {seasons.map((s) => (
                  <div key={s} className={`flex items-center gap-2 border rounded-full pl-3 pr-2 py-1.5 transition-colors ${seasonFilter === s ? "bg-gold/15 border-gold" : "bg-background border-white/15"}`} data-testid={`season-chip-${s}`}>
                    {renaming.old === s ? (
                      <input
                        autoFocus
                        value={renaming.val}
                        onChange={(e) => setRenaming({ old: s, val: e.target.value })}
                        onBlur={() => commitRename(s, renaming.val)}
                        onKeyDown={(e) => { if (e.key === "Enter") commitRename(s, renaming.val); if (e.key === "Escape") setRenaming({ old: "", val: "" }); }}
                        className="bg-transparent border-b border-gold text-white text-sm outline-none w-32"
                        data-testid={`season-rename-input-${s}`}
                      />
                    ) : (
                      <button onClick={() => setSeasonFilter(s)} className={`text-sm cond-font tracking-wide transition-colors ${seasonFilter === s ? "text-gold" : "text-white hover:text-gold"}`} title="Click to view this season's players" data-testid={`season-filter-${s}`}>{s}</button>
                    )}
                    <span className="text-white/30 text-xs">{rows.filter((r) => (r.seasons || []).includes(s)).length}</span>
                    <button onClick={() => setRenaming({ old: s, val: s })} className="text-white/40 hover:text-gold transition-colors" title="Rename season" data-testid={`season-rename-${s}`}><Pencil size={12} /></button>
                    <button onClick={() => deleteSeason(s)} className="text-white/40 hover:text-red-400 transition-colors" title="Delete season" data-testid={`season-delete-${s}`}><X size={14} /></button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex items-center justify-between flex-wrap gap-4 mb-6">
            <div className="flex items-center gap-3 text-white">
              <div className="w-11 h-11 rounded-md bg-gold/15 border border-gold/30 flex items-center justify-center text-gold">
                <Users size={20} />
              </div>
              <div>
                <div className="display-font text-2xl text-gold leading-none">{filtered.length}</div>
                <div className="cond-font uppercase tracking-widest text-[10px] text-white/50">Showing of {rows.length}</div>
              </div>
            </div>
            <div className="flex items-end flex-wrap gap-3">
              <div className="relative">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email, level..." className="bg-card border-white/15 pl-9 w-64" data-testid="filter-search" />
              </div>
              <div>
                <Label className="cond-font uppercase tracking-wide text-[10px] text-white/50 mb-1 block">From</Label>
                <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="bg-card border-white/15 text-white h-10" data-testid="filter-date-from" />
              </div>
              <div>
                <Label className="cond-font uppercase tracking-wide text-[10px] text-white/50 mb-1 block">To</Label>
                <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="bg-card border-white/15 text-white h-10" data-testid="filter-date-to" />
              </div>
              <div className="w-40">
                <Label className="cond-font uppercase tracking-wide text-[10px] text-white/50 mb-1 block">Level</Label>
                <Select value={levelFilter} onValueChange={setLevelFilter}>
                  <SelectTrigger className="bg-card border-white/15 h-10" data-testid="filter-level"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All levels</SelectItem>
                    {playingLevels.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="w-44">
                <Label className="cond-font uppercase tracking-wide text-[10px] text-white/50 mb-1 block">Season</Label>
                <Select value={seasonFilter} onValueChange={setSeasonFilter}>
                  <SelectTrigger className="bg-card border-white/15 h-10" data-testid="filter-season"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No group (unassigned)</SelectItem>
                    <SelectItem value="all">All seasons</SelectItem>
                    {seasons.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {(dateFrom || dateTo || levelFilter !== "all" || seasonFilter !== "none" || q) && (
                <button
                  onClick={() => { setQ(""); setDateFrom(""); setDateTo(""); setLevelFilter("all"); setSeasonFilter("none"); }}
                  className="cond-font uppercase tracking-widest text-xs text-white/50 hover:text-gold h-10 px-2 transition-colors"
                  data-testid="filter-clear"
                >
                  Clear
                </button>
              )}
            </div>
          </div>

          {selectedIds.size > 0 && (
            <div className="flex items-center flex-wrap gap-3 mb-4 bg-gold/10 border border-gold/30 rounded-lg px-4 py-3" data-testid="bulk-bar">
              <span className="cond-font uppercase tracking-widest text-xs text-gold">{selectedIds.size} selected</span>
              <div className="w-52">
                <Select value={bulkSeason} onValueChange={setBulkSeason}>
                  <SelectTrigger className="bg-background border-white/15 h-9" data-testid="bulk-season-select"><SelectValue placeholder="Choose a season" /></SelectTrigger>
                  <SelectContent>
                    {seasons.length === 0 ? <SelectItem value="__none" disabled>No seasons — add one above</SelectItem> : seasons.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <button onClick={() => bulkAssign("add")} className="inline-flex items-center gap-2 bg-gold text-black cond-font uppercase tracking-widest text-xs font-semibold px-4 py-2 rounded-sm hover:bg-gold-light transition-colors" data-testid="bulk-add-btn"><Plus size={14} /> Add to season</button>
              <button onClick={() => bulkAssign("remove")} className="inline-flex items-center gap-2 border border-white/25 text-white/80 cond-font uppercase tracking-widest text-xs px-4 py-2 rounded-sm hover:border-red-400 hover:text-red-400 transition-colors" data-testid="bulk-remove-btn">Remove from season</button>
              <button onClick={() => setSelectedIds(new Set())} className="cond-font uppercase tracking-widest text-xs text-white/40 hover:text-white ml-auto" data-testid="bulk-clear-btn">Clear</button>
            </div>
          )}

          {filtered.length === 0 ? (
            <div className="border border-dashed border-white/15 rounded-xl py-20 text-center text-white/40">
              <Users size={40} className="mx-auto mb-4 opacity-40" />
              <p className="font-light">
                {seasonFilter === "none"
                  ? "No unassigned players — everyone is in a group. Pick a season chip above to view its players."
                  : seasonFilter !== "all"
                  ? "No players in this season yet. Select players (under \"No group\") and add them to this season."
                  : "No registrations match. Submissions from the site will appear here."}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto xl:overflow-x-visible border border-white/10 rounded-xl">
              <table className="w-full text-left">
                <colgroup>
                  <col style={{ width: "40px" }} />
                  <col style={{ width: "140px" }} />
                  <col style={{ width: "90px" }} />
                  <col style={{ width: "110px" }} />
                  <col style={{ width: "200px" }} />
                  <col style={{ width: "130px" }} />
                  <col style={{ width: "150px" }} />
                  <col style={{ width: "120px" }} />
                  <col />
                  <col style={{ width: "72px" }} />
                </colgroup>
                <thead className="bg-card border-b border-white/10">
                  <tr className="cond-font uppercase tracking-widest text-[11px] text-white/50">
                    <th className="px-4 py-4 w-10">
                      <input
                        type="checkbox"
                        className="accent-gold w-4 h-4 align-middle cursor-pointer"
                        data-testid="select-all"
                        checked={filtered.length > 0 && filtered.every((r) => selectedIds.has(r.id))}
                        onChange={(e) => toggleSelectAll(filtered.map((r) => r.id), e.target.checked)}
                      />
                    </th>
                    <th className="px-5 py-4">Player</th>
                    <th className="px-5 py-4">Level</th>
                    <th className="px-5 py-4">Position</th>
                    <th className="px-5 py-4">Email</th>
                    <th className="px-5 py-4">Phone</th>
                    <th className="px-5 py-4">Status</th>
                    <th className="px-5 py-4">Seasons</th>
                    <th className="px-5 py-4">Notes</th>
                    <th className="px-5 py-4"></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((r) => (
                    <tr key={r.id} className={`border-b border-white/5 hover:bg-white/[0.02] transition-colors ${selectedIds.has(r.id) ? "bg-gold/[0.06]" : ""}`} data-testid={`reg-row-${r.id}`}>
                      <td className="px-4 py-4">
                        <input
                          type="checkbox"
                          className="accent-gold w-4 h-4 align-middle cursor-pointer"
                          data-testid={`select-${r.id}`}
                          checked={selectedIds.has(r.id)}
                          onChange={() => toggleSelect(r.id)}
                        />
                      </td>
                      <td className="px-5 py-4 text-white font-medium">{r.playerFirstName} {r.playerLastName}</td>
                      <td className="px-5 py-4 text-white/70 font-light">{r.playingLevel || "—"}</td>
                      <td className="px-5 py-4 text-white/70 font-light">{r.position || "—"}</td>
                      <td className="px-5 py-4 text-white/70 font-light">
                        <a
                          href={`mailto:${r.email}`}
                          title={r.email}
                          className="block max-w-[200px] truncate hover:text-gold transition-colors"
                          data-testid={`email-cell-${r.id}`}
                        >
                          {r.email}
                        </a>
                      </td>
                      <td className="px-5 py-4 text-white/70 font-light">{r.phone}</td>
                      <td className="px-5 py-4">
                        <Select value={r.status || "New"} onValueChange={(v) => updateStatus(r.id, v)}>
                          <SelectTrigger className={`w-36 h-9 bg-background text-xs cond-font uppercase tracking-widest ${statusClass[r.status || "New"]}`} data-testid={`status-select-${r.id}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="px-5 py-4">
                        {(r.seasons || []).length === 0 ? (
                          <span className="text-white/25 text-sm">—</span>
                        ) : (
                          <div className="flex flex-wrap gap-1 max-w-[180px]" data-testid={`seasons-cell-${r.id}`}>
                            {(r.seasons || []).map((s) => (
                              <span key={s} className="inline-block text-[10px] cond-font uppercase tracking-wide text-gold border border-gold/40 rounded-full px-2 py-0.5">{s}</span>
                            ))}
                          </div>
                        )}
                      </td>
                      <td className="px-5 py-4">
                        <Input
                          defaultValue={r.notes || ""}
                          onBlur={(e) => { if (e.target.value !== (r.notes || "")) updateNotes(r.id, e.target.value); }}
                          placeholder="Add a note…"
                          className="w-full min-w-[150px] h-9 bg-background border-white/15 text-sm"
                          data-testid={`notes-input-${r.id}`}
                        />
                      </td>
                      <td className="px-5 py-4 text-right whitespace-nowrap">
                        <button onClick={() => setEditRow({ ...r, seasons: r.seasons || [] })} className="text-white/40 hover:text-gold transition-colors mr-3" title="Edit player" data-testid={`edit-reg-${r.id}`}>
                          <Pencil size={16} />
                        </button>
                        <button onClick={() => deleteRegistration(r.id)} className="text-white/40 hover:text-red-400 transition-colors" title="Delete registration" data-testid={`delete-reg-${r.id}`}>
                          <Trash2 size={16} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {editRow && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" data-testid="edit-modal" onMouseDown={(e) => { if (e.target === e.currentTarget) setEditRow(null); }}>
              <div className="bg-card border border-white/15 rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                <div className="flex items-center justify-between px-6 py-4 border-b border-white/10 sticky top-0 bg-card z-10">
                  <h3 className="display-font text-xl text-gold">Edit Player</h3>
                  <button onClick={() => setEditRow(null)} className="text-white/50 hover:text-white" data-testid="edit-close"><X size={20} /></button>
                </div>
                <div className="p-6 space-y-4">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <FieldA label="First Name"><Input value={editRow.playerFirstName || ""} onChange={(e) => setEditRow({ ...editRow, playerFirstName: e.target.value })} className="bg-background border-white/15" data-testid="edit-first" /></FieldA>
                    <FieldA label="Last Name"><Input value={editRow.playerLastName || ""} onChange={(e) => setEditRow({ ...editRow, playerLastName: e.target.value })} className="bg-background border-white/15" data-testid="edit-last" /></FieldA>
                    <FieldA label="Date of Birth"><Input value={editRow.dob || ""} onChange={(e) => setEditRow({ ...editRow, dob: e.target.value })} placeholder="YYYY-MM-DD" className="bg-background border-white/15" data-testid="edit-dob" /></FieldA>
                    <FieldA label="Position"><Input value={editRow.position || ""} onChange={(e) => setEditRow({ ...editRow, position: e.target.value })} className="bg-background border-white/15" data-testid="edit-position" /></FieldA>
                    <FieldA label="Playing Level">
                      <Select value={editRow.playingLevel || ""} onValueChange={(v) => setEditRow({ ...editRow, playingLevel: v })}>
                        <SelectTrigger className="bg-background border-white/15" data-testid="edit-level"><SelectValue placeholder="Select level" /></SelectTrigger>
                        <SelectContent>{playingLevels.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
                      </Select>
                    </FieldA>
                    <FieldA label="Status">
                      <Select value={editRow.status || "New"} onValueChange={(v) => setEditRow({ ...editRow, status: v })}>
                        <SelectTrigger className="bg-background border-white/15" data-testid="edit-status"><SelectValue /></SelectTrigger>
                        <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                      </Select>
                    </FieldA>
                    <FieldA label="Email"><Input value={editRow.email || ""} onChange={(e) => setEditRow({ ...editRow, email: e.target.value })} className="bg-background border-white/15" data-testid="edit-email" /></FieldA>
                    <FieldA label="Phone"><Input value={editRow.phone || ""} onChange={(e) => setEditRow({ ...editRow, phone: e.target.value })} className="bg-background border-white/15" data-testid="edit-phone" /></FieldA>
                    <FieldA label="Guardian Name"><Input value={editRow.guardianName || ""} onChange={(e) => setEditRow({ ...editRow, guardianName: e.target.value })} className="bg-background border-white/15" data-testid="edit-guardian" /></FieldA>
                    <FieldA label="Emergency Contact"><Input value={editRow.emergencyContact || ""} onChange={(e) => setEditRow({ ...editRow, emergencyContact: e.target.value })} className="bg-background border-white/15" data-testid="edit-emergency" /></FieldA>
                    <FieldA label="Emergency Phone"><Input value={editRow.emergencyPhone || ""} onChange={(e) => setEditRow({ ...editRow, emergencyPhone: e.target.value })} className="bg-background border-white/15" data-testid="edit-emergency-phone" /></FieldA>
                  </div>
                  <FieldA label="Medical Notes"><textarea value={editRow.medicalNotes || ""} onChange={(e) => setEditRow({ ...editRow, medicalNotes: e.target.value })} rows={2} className="w-full bg-background border border-white/15 rounded-md px-3 py-2 text-sm text-white resize-y" data-testid="edit-medical" /></FieldA>
                  <FieldA label="Coach Notes"><textarea value={editRow.notes || ""} onChange={(e) => setEditRow({ ...editRow, notes: e.target.value })} rows={2} className="w-full bg-background border border-white/15 rounded-md px-3 py-2 text-sm text-white resize-y" data-testid="edit-notes" /></FieldA>
                  <FieldA label="Player Photo (public Teams page)">
                    <div className="flex items-center gap-4">
                      <div className="w-16 h-16 rounded-full overflow-hidden bg-black/60 border border-white/15 flex items-center justify-center shrink-0">
                        {editRow.photoUrl ? <img src={editRow.photoUrl} alt="" className="w-full h-full object-cover" data-testid="edit-photo-preview" /> : <ImageIcon size={20} className="text-white/25" />}
                      </div>
                      <label className="cursor-pointer inline-flex items-center gap-2 border border-white/25 text-white cond-font uppercase tracking-widest text-xs px-4 py-2.5 rounded-sm hover:border-gold hover:text-gold transition-colors" data-testid="edit-photo-upload">
                        {editPhotoUploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                        {editPhotoUploading ? "Uploading..." : editRow.photoUrl ? "Change photo" : "Upload headshot"}
                        <input type="file" accept="image/*" className="hidden" disabled={editPhotoUploading} onChange={(e) => uploadEditPhoto(e.target.files?.[0])} />
                      </label>
                      {editRow.photoUrl && (
                        <button type="button" onClick={() => setEditRow({ ...editRow, photoUrl: "" })} className="text-white/40 hover:text-red-400 text-xs cond-font uppercase tracking-widest" data-testid="edit-photo-remove">Remove</button>
                      )}
                    </div>
                  </FieldA>
                  <FieldA label="Seasons">
                    {seasons.length === 0 ? (
                      <p className="text-white/40 text-sm font-light">No seasons yet — add some in the Seasons panel above.</p>
                    ) : (
                      <div className="flex flex-wrap gap-2" data-testid="edit-seasons">
                        {seasons.map((s) => {
                          const on = (editRow.seasons || []).includes(s);
                          return (
                            <button key={s} type="button" onClick={() => toggleEditSeason(s)} className={`text-xs cond-font uppercase tracking-wide rounded-full px-3 py-1.5 border transition-colors ${on ? "bg-gold text-black border-gold" : "text-white/70 border-white/20 hover:border-gold"}`} data-testid={`edit-season-${s}`}>{s}</button>
                          );
                        })}
                      </div>
                    )}
                  </FieldA>
                </div>
                <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-white/10 sticky bottom-0 bg-card">
                  <button onClick={() => setEditRow(null)} className="cond-font uppercase tracking-widest text-xs text-white/60 hover:text-white px-4 py-2.5" data-testid="edit-cancel">Cancel</button>
                  <button onClick={saveEdit} disabled={savingEdit} className="inline-flex items-center gap-2 bg-gold text-black cond-font uppercase tracking-widest text-xs font-semibold px-5 py-2.5 rounded-sm hover:bg-gold-light transition-colors disabled:opacity-60" data-testid="edit-save">
                    {savingEdit ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} {savingEdit ? "Saving..." : "Save Player"}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {tab === "users" && isAdmin && (
        <div className="max-w-4xl mx-auto px-5 py-8 space-y-8">
          <div className="bg-card border border-white/10 rounded-xl p-7">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-11 h-11 rounded-md bg-gold/15 border border-gold/30 flex items-center justify-center text-gold"><UserCog size={20} /></div>
              <div>
                <h3 className="cond-font uppercase tracking-wide text-lg text-white leading-none">Add a User</h3>
                <p className="text-white/40 text-xs font-light mt-1">Administrators manage everything. Users can only edit Live & Video.</p>
              </div>
            </div>
            <form onSubmit={createUser} className="grid sm:grid-cols-2 gap-4" data-testid="add-user-form">
              <FieldA label="Email"><Input type="email" required value={newUser.email} onChange={(e) => setNewUser((u) => ({ ...u, email: e.target.value }))} className="bg-background border-white/15" data-testid="newuser-email" /></FieldA>
              <FieldA label="Name"><Input value={newUser.name} onChange={(e) => setNewUser((u) => ({ ...u, name: e.target.value }))} className="bg-background border-white/15" data-testid="newuser-name" /></FieldA>
              <FieldA label="Password (min 6)"><Input type="password" required value={newUser.password} onChange={(e) => setNewUser((u) => ({ ...u, password: e.target.value }))} className="bg-background border-white/15" data-testid="newuser-password" /></FieldA>
              <FieldA label="Role">
                <Select value={newUser.role} onValueChange={(v) => setNewUser((u) => ({ ...u, role: v }))}>
                  <SelectTrigger className="bg-background border-white/15" data-testid="newuser-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="user">User (Video only)</SelectItem>
                    <SelectItem value="admin">Administrator</SelectItem>
                  </SelectContent>
                </Select>
              </FieldA>
              <div className="sm:col-span-2">
                <button type="submit" className="inline-flex items-center gap-2 bg-gold text-black cond-font uppercase tracking-widest text-xs font-semibold px-5 py-3 rounded-sm hover:bg-gold-light transition-colors" data-testid="add-user-btn">
                  <Plus size={15} /> Add User
                </button>
              </div>
            </form>
          </div>

          <div className="border border-white/10 rounded-xl overflow-hidden">
            <table className="w-full text-left">
              <thead className="bg-card border-b border-white/10">
                <tr className="cond-font uppercase tracking-widest text-[11px] text-white/50">
                  <th className="px-5 py-4">Email</th>
                  <th className="px-5 py-4">Name</th>
                  <th className="px-5 py-4">Role</th>
                  <th className="px-5 py-4"></th>
                </tr>
              </thead>
              <tbody data-testid="users-table">
                {users.map((u) => (
                  <tr key={u.id} className="border-b border-white/5">
                    <td className="px-5 py-4 text-white font-medium">{u.email}</td>
                    <td className="px-5 py-4 text-white/70 font-light">{u.name || "—"}</td>
                    <td className="px-5 py-4">
                      <span className={`inline-flex items-center gap-1 cond-font uppercase tracking-widest text-[11px] px-2.5 py-1 rounded-full border ${u.role === "admin" ? "text-gold border-gold/40" : "text-white/60 border-white/20"}`}>
                        {u.role === "admin" && <Shield size={12} />} {u.role}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-right">
                      {u.id !== user.id && (
                        <button onClick={() => deleteUser(u.id)} className="text-white/40 hover:text-red-400 transition-colors" data-testid={`delete-user-${u.email}`}>
                          <Trash2 size={16} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "media" && settings && (
        <div className="max-w-4xl mx-auto px-5 py-8 space-y-8">
          {/* Live Stream */}
          <div className="bg-card border border-white/10 rounded-xl p-7">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-md bg-red-500/15 border border-red-500/40 flex items-center justify-center text-red-400">
                  <Radio size={20} />
                </div>
                <div>
                  <h3 className="cond-font uppercase tracking-wide text-lg text-white leading-none">Live Stream</h3>
                  <p className="text-white/40 text-xs font-light mt-1">Shown in the site's Live section</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className="cond-font uppercase tracking-widest text-xs text-white/60">{settings.live.enabled ? "On Air" : "Offline"}</span>
                <Switch checked={settings.live.enabled} onCheckedChange={(v) => setLive("enabled", v)} className="data-[state=checked]:bg-red-500" data-testid="live-toggle" />
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4 mt-6">
              <FieldA label="YouTube Live URL or ID">
                <Input value={settings.live.videoId} onChange={(e) => setLive("videoId", e.target.value)} placeholder="https://youtube.com/live/…" className="bg-background border-white/15" />
              </FieldA>
              <FieldA label="Title">
                <Input value={settings.live.title} onChange={(e) => setLive("title", e.target.value)} className="bg-background border-white/15" data-testid="live-title" />
              </FieldA>
              <FieldA label="Location / Venue">
                <Input value={settings.live.location} onChange={(e) => setLive("location", e.target.value)} placeholder="e.g. Central Broward Park, FL" className="bg-background border-white/15" />
              </FieldA>
              <FieldA label="Schedule Note (optional)">
                <Input value={settings.live.scheduledText} onChange={(e) => setLive("scheduledText", e.target.value)} placeholder="e.g. Sat 7:00 PM ET" className="bg-background border-white/15" />
              </FieldA>
            </div>
          </div>

          {/* Background videos */}
          <div className="bg-card border border-white/10 rounded-xl p-7">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-md bg-gold/15 border border-gold/30 flex items-center justify-center text-gold">
                <Video size={20} />
              </div>
              <div>
                <h3 className="cond-font uppercase tracking-wide text-lg text-white leading-none">Background Videos</h3>
                <p className="text-white/40 text-xs font-light mt-1">Upload a clip or paste a link · add loops · logo between loops</p>
              </div>
            </div>

            <div className="grid lg:grid-cols-2 gap-8 mt-6">
              <VideoSectionEditor
                title="Hero Header"
                type={settings.heroVideoType}
                source={settings.heroVideoId}
                loops={settings.heroLoops || []}
                logoBetween={settings.heroLogoBetween !== false}
                uploading={uploading.hero}
                onType={(v) => setS("heroVideoType", v)}
                onSource={(v) => setS("heroVideoId", v)}
                onLoops={(v) => setS("heroLoops", v)}
                onLogo={(v) => setS("heroLogoBetween", v)}
                onUpload={(f) => uploadVideo("hero", f)}
                onUploadImage={(f) => uploadSectionImage("hero", f)}
                onUseDefault={() => applyDefaultImage("hero")}
              />
              <VideoSectionEditor
                title="Registration / Tryout"
                type={settings.registrationVideoType}
                source={settings.registrationVideoId}
                loops={settings.registrationLoops || []}
                logoBetween={settings.registrationLogoBetween === true}
                uploading={uploading.registration}
                onType={(v) => setS("registrationVideoType", v)}
                onSource={(v) => setS("registrationVideoId", v)}
                onLoops={(v) => setS("registrationLoops", v)}
                onLogo={(v) => setS("registrationLogoBetween", v)}
                onUpload={(f) => uploadVideo("registration", f)}
                onUploadImage={(f) => uploadSectionImage("registration", f)}
                onUseDefault={() => applyDefaultImage("registration")}
              />
            </div>
            <p className="text-white/35 text-xs font-light mt-6">
              Loops only apply to <span className="text-gold">YouTube</span> videos (enter times as mm:ss or h:mm:ss).
              With 2+ loops and "logo between loops" on, the crest twirls while the next clip buffers. MP4 clips loop seamlessly on their own.
            </p>

            {/* Live loop preview */}
            {settings.heroVideoType === "youtube" && settings.heroVideoId && (
              <div className="mt-6">
                <p className="cond-font uppercase tracking-widest text-xs text-gold mb-3">Header Loop Preview</p>
                <div className="relative w-full max-w-md aspect-video rounded-lg overflow-hidden border border-white/10 bg-black" data-testid="loop-preview">
                  <HeroVideoLoop
                    key={JSON.stringify(settings.heroLoops) + settings.heroVideoId + settings.heroLogoBetween}
                    videoId={settings.heroVideoId}
                    loops={settings.heroLoops || []}
                    logoBetween={settings.heroLogoBetween !== false}
                  />
                </div>
                <p className="text-white/35 text-xs font-light mt-2">Preview reflects unsaved edits. Save Changes to apply to the live site.</p>
              </div>
            )}
          </div>

          {/* Hero video playlist */}
          <div className="bg-card border border-white/10 rounded-xl p-7" data-testid="hero-playlist-card">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-md bg-gold/15 border border-gold/30 flex items-center justify-center text-gold"><Video size={20} /></div>
                <div>
                  <h3 className="cond-font uppercase tracking-wide text-lg text-white leading-none">Hero Video Playlist</h3>
                  <p className="text-white/40 text-xs font-light mt-1">Add multiple header clips (YouTube or MP4). They loop one after another. Overrides the single Hero Header source above when 1+ clips are added.</p>
                </div>
              </div>
              <button onClick={addHeroClip} className="inline-flex items-center gap-2 border border-white/25 text-white cond-font uppercase tracking-widest text-xs px-4 py-2.5 rounded-sm hover:border-gold hover:text-gold transition-colors" data-testid="hero-clip-add-btn">
                <Plus size={15} /> Add Clip
              </button>
            </div>

            {(settings.heroPlaylist || []).length === 0 ? (
              <p className="text-white/40 text-sm font-light mt-6">No playlist clips — the single Hero Header source above is used. Add 2+ clips to cycle through them on the homepage header.</p>
            ) : (
              <div className="space-y-4 mt-6" data-testid="hero-clip-items">
                {(settings.heroPlaylist || []).map((c, i) => (
                  <div
                    key={i}
                    draggable
                    onDragStart={() => setHeroDragIndex(i)}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={() => { if (heroDragIndex !== -1 && heroDragIndex !== i) moveHeroClip(heroDragIndex, i); setHeroDragIndex(-1); }}
                    className={`border rounded-lg p-4 flex gap-3 items-start transition-colors ${heroDragIndex === i ? "border-gold bg-gold/5" : "border-white/10"}`}
                    data-testid={`hero-clip-${i}`}
                  >
                    <div className="flex items-center pt-2 text-white/30 cursor-grab active:cursor-grabbing" title="Drag to reorder" data-testid={`hero-clip-drag-${i}`}>
                      <GripVertical size={18} />
                    </div>
                    <div className="w-16 h-10 rounded overflow-hidden bg-black/60 border border-white/10 flex items-center justify-center shrink-0" data-testid={`hero-clip-thumb-${i}`}>
                      {c.type === "youtube" && extractYouTubeId(c.source) ? (
                        <img src={`https://img.youtube.com/vi/${extractYouTubeId(c.source)}/mqdefault.jpg`} alt="" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                      ) : c.type === "mp4" && c.source ? (
                        <video src={c.source} muted playsInline preload="metadata" className="w-full h-full object-cover" />
                      ) : (
                        <Video size={16} className="text-white/25" />
                      )}
                    </div>
                    <div className="flex-1 space-y-3">
                      <div className="grid sm:grid-cols-[120px_1fr_150px] gap-3">
                        <Select value={c.type || "youtube"} onValueChange={(v) => updateHeroClip(i, "type", v)}>
                          <SelectTrigger className="bg-background border-white/15 h-9" data-testid={`hero-clip-type-${i}`}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="youtube">YouTube</SelectItem>
                            <SelectItem value="mp4">MP4</SelectItem>
                          </SelectContent>
                        </Select>
                        <Input value={c.source || ""} onChange={(e) => updateHeroClip(i, "source", e.target.value)} placeholder={c.type === "mp4" ? "MP4 URL" : "YouTube URL or ID"} className="bg-background border-white/15 h-9 text-sm" data-testid={`hero-clip-src-${i}`} />
                        <Select value={c.schedule || "always"} onValueChange={(v) => updateHeroClip(i, "schedule", v)}>
                          <SelectTrigger className="bg-background border-white/15 h-9" data-testid={`hero-clip-schedule-${i}`}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="always">Show: Always</SelectItem>
                            <SelectItem value="normal">Normal weeks</SelectItem>
                            <SelectItem value="match">Match days</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      {c.type === "mp4" && (
                        <label className="cursor-pointer inline-flex items-center gap-2 border border-white/25 text-white/70 cond-font uppercase tracking-widest text-xs px-3 py-2 rounded-sm hover:border-gold hover:text-gold transition-colors w-fit" data-testid={`hero-clip-upload-${i}`}>
                          {heroClipUploading === i ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
                          {heroClipUploading === i ? "Uploading..." : "Upload MP4"}
                          <input type="file" accept="video/*" className="hidden" disabled={heroClipUploading === i} onChange={(e) => uploadHeroClip(i, e.target.files?.[0])} />
                        </label>
                      )}
                      {c.type === "youtube" && (
                        <div className="border border-white/10 rounded-md p-3 space-y-2" data-testid={`hero-clip-loops-${i}`}>
                          <div className="flex items-center justify-between">
                            <span className="cond-font uppercase tracking-widest text-[11px] text-white/60">Loop Windows (mm:ss)</span>
                            <button onClick={() => addHeroClipLoop(i)} className="inline-flex items-center gap-1 text-gold text-xs cond-font uppercase tracking-widest hover:text-gold-light" data-testid={`hero-clip-loop-add-${i}`}>
                              <Plus size={13} /> Add
                            </button>
                          </div>
                          {(c.loops || []).length === 0 && <p className="text-white/40 text-xs font-light">No windows — the full video plays before moving to the next clip.</p>}
                          {(c.loops || []).map((l, li) => (
                            <div
                              key={`${li}-${l.start}-${l.end}`}
                              onDragOver={(e) => e.preventDefault()}
                              onDrop={() => { if (loopDrag.clip === i && loopDrag.idx !== -1 && loopDrag.idx !== li) moveHeroClipLoop(i, loopDrag.idx, li); setLoopDrag({ clip: -1, idx: -1 }); }}
                              className={`flex items-center gap-2 rounded-md transition-colors ${loopDrag.clip === i && loopDrag.idx === li ? "opacity-40" : ""}`}
                              data-testid={`hero-clip-loop-row-${i}-${li}`}
                            >
                              <span
                                draggable
                                onDragStart={() => setLoopDrag({ clip: i, idx: li })}
                                onDragEnd={() => setLoopDrag({ clip: -1, idx: -1 })}
                                className="text-white/25 hover:text-white/50 cursor-grab active:cursor-grabbing shrink-0"
                                title="Drag to reorder"
                                data-testid={`hero-clip-loop-drag-${i}-${li}`}
                              >
                                <GripVertical size={15} />
                              </span>
                              <Input defaultValue={secToClock(l.start)} onBlur={(e) => updateHeroClipLoop(i, li, "start", e.target.value)} placeholder="mm:ss" className="bg-background border-white/15 h-9 text-sm" data-testid={`hero-clip-loop-start-${i}-${li}`} />
                              <span className="text-white/40 text-xs">→</span>
                              <Input defaultValue={secToClock(l.end)} onBlur={(e) => updateHeroClipLoop(i, li, "end", e.target.value)} placeholder="mm:ss" className="bg-background border-white/15 h-9 text-sm" data-testid={`hero-clip-loop-end-${i}-${li}`} />
                              <button onClick={() => removeHeroClipLoop(i, li)} className="text-white/40 hover:text-red-400 p-1 shrink-0" data-testid={`hero-clip-loop-remove-${i}-${li}`}><Trash2 size={15} /></button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <button onClick={() => removeHeroClip(i)} className="text-white/40 hover:text-red-400 p-1 mt-1" data-testid={`hero-clip-remove-${i}`}><Trash2 size={15} /></button>
                  </div>
                ))}
                <div className="flex items-center justify-between border border-white/10 rounded-lg px-4 py-3">
                  <span className="cond-font uppercase tracking-widest text-[11px] text-white/60">Twirl crest between clips</span>
                  <Switch checked={settings.heroLogoBetween !== false} onCheckedChange={(v) => setS("heroLogoBetween", v)} className="data-[state=checked]:bg-gold" data-testid="hero-clip-twirl" />
                </div>
              </div>
            )}

            {/* Match days */}
            <div className="mt-6 border border-white/10 rounded-lg p-4 bg-background/40" data-testid="match-days-card">
              <div className="flex items-center gap-2 mb-2">
                <CalendarClock size={15} className="text-gold" />
                <span className="cond-font uppercase tracking-widest text-xs text-gold">Match Days</span>
              </div>
              <p className="text-white/35 text-xs font-light mb-3">Dates you add here use the "Match days" clips; every other day uses "Normal weeks" clips. "Always" clips show on both.</p>
              <div className="flex items-center gap-2">
                <Input type="date" value={newMatchDay} onChange={(e) => setNewMatchDay(e.target.value)} className="bg-background border-white/15 h-9 w-48 text-white" data-testid="match-day-input" />
                <button onClick={addMatchDay} className="inline-flex items-center gap-1.5 border border-white/25 text-white cond-font uppercase tracking-widest text-xs px-4 py-2 rounded-sm hover:border-gold hover:text-gold transition-colors" data-testid="match-day-add"><Plus size={14} /> Add</button>
              </div>
              {(settings.matchDays || []).length > 0 && (
                <div className="flex flex-wrap gap-2 mt-3" data-testid="match-day-list">
                  {(settings.matchDays || []).slice().sort().map((d) => (
                    <span key={d} className="inline-flex items-center gap-2 border border-white/15 rounded-full px-3 py-1 text-xs text-white/70" data-testid={`match-day-${d}`}>
                      {d}<button onClick={() => removeMatchDay(d)} className="text-white/40 hover:text-red-400" data-testid={`match-day-remove-${d}`}><X size={12} /></button>
                    </span>
                  ))}
                </div>
              )}

              {/* Fixtures — dates auto-count as match days */}
              <div className="mt-5 pt-4 border-t border-white/10">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Flag size={14} className="text-gold" />
                    <span className="cond-font uppercase tracking-widest text-xs text-gold">Fixtures</span>
                  </div>
                  <button onClick={addFixture} className="inline-flex items-center gap-1 text-gold text-xs cond-font uppercase tracking-widest hover:text-gold-light" data-testid="fixture-add"><Plus size={13} /> Add Fixture</button>
                </div>
                <p className="text-white/35 text-xs font-light mb-3">Add your match schedule here — every fixture date is <b className="text-white/60">automatically treated as a match day</b>, so you never type dates by hand.</p>
                {(settings.fixtures || []).length === 0 ? (
                  <p className="text-white/40 text-xs font-light">No fixtures yet.</p>
                ) : (
                  <div className="space-y-2" data-testid="fixture-list">
                    {(settings.fixtures || []).map((f, fi) => (
                      <div key={fi} className="grid grid-cols-1 sm:grid-cols-[150px_1fr_100px_120px_36px] gap-2 items-center" data-testid={`fixture-${fi}`}>
                        <Input type="date" value={f.date || ""} onChange={(e) => updateFixture(fi, "date", e.target.value)} className="bg-background border-white/15 h-9 text-sm text-white" data-testid={`fixture-date-${fi}`} />
                        <Input value={f.opponent || ""} onChange={(e) => updateFixture(fi, "opponent", e.target.value)} placeholder="Opponent" className="bg-background border-white/15 h-9 text-sm" data-testid={`fixture-opp-${fi}`} />
                        <Input value={f.time || ""} onChange={(e) => updateFixture(fi, "time", e.target.value)} placeholder="7:30 PM" className="bg-background border-white/15 h-9 text-sm" data-testid={`fixture-time-${fi}`} />
                        <Select value={f.home ? "home" : "away"} onValueChange={(v) => updateFixture(fi, "home", v === "home")}>
                          <SelectTrigger className="bg-background border-white/15 h-9" data-testid={`fixture-venue-${fi}`}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="home">Home</SelectItem>
                            <SelectItem value="away">Away</SelectItem>
                          </SelectContent>
                        </Select>
                        <button onClick={() => removeFixture(fi)} className="text-white/40 hover:text-red-400 p-1 justify-self-start" data-testid={`fixture-remove-${fi}`}><Trash2 size={15} /></button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Full playlist preview */}
            {(settings.heroPlaylist || []).length > 0 && (
              <div className="mt-6" data-testid="playlist-preview-wrap">
                <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                  <p className="cond-font uppercase tracking-widest text-xs text-gold">Header Loop Preview</p>
                  <div className="inline-flex rounded-md border border-white/15 overflow-hidden">
                    {[{ v: "normal", l: "Normal week" }, { v: "match", l: "Match day" }].map((m) => (
                      <button key={m.v} onClick={() => setPreviewMode(m.v)} className={`cond-font uppercase tracking-widest text-[11px] px-4 py-2 transition-colors ${previewMode === m.v ? "bg-gold text-black" : "bg-transparent text-white/60 hover:text-white"}`} data-testid={`playlist-preview-${m.v}`}>{m.l}</button>
                    ))}
                  </div>
                </div>
                {previewClips.length === 0 ? (
                  <p className="text-white/40 text-sm font-light">No clips scheduled for {previewMode === "match" ? "match days" : "normal weeks"} — the single Hero Header source is used instead.</p>
                ) : (
                  <div className="relative w-full max-w-md aspect-video rounded-lg overflow-hidden border border-white/10 bg-black" data-testid="playlist-preview">
                    <HeroPlaylist
                      key={JSON.stringify(previewClips) + previewMode + settings.heroLogoBetween}
                      clips={previewClips}
                      logoBetween={settings.heroLogoBetween !== false}
                    />
                  </div>
                )}
                <p className="text-white/35 text-xs font-light mt-2">Plays the entire playlist exactly as the live header will. Toggle to preview the normal-week vs match-day rotation. Save Changes to go live.</p>
              </div>
            )}
          </div>

          {/* Club Photos (Gallery) manager */}
          <div className="bg-card border border-white/10 rounded-xl p-7" data-testid="gallery-manager-card">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-md bg-gold/15 border border-gold/30 flex items-center justify-center text-gold"><ImageIcon size={20} /></div>
                <div>
                  <h3 className="cond-font uppercase tracking-wide text-lg text-white leading-none">Club Photos (Gallery)</h3>
                  <p className="text-white/40 text-xs font-light mt-1">Upload your team photos + captions. Shown in the site's "Club photos" section — the first photo is featured larger. Drag to reorder.</p>
                </div>
              </div>
              <button onClick={addGalleryItem} className="inline-flex items-center gap-2 border border-white/25 text-white cond-font uppercase tracking-widest text-xs px-4 py-2.5 rounded-sm hover:border-gold hover:text-gold transition-colors" data-testid="gallery-add-btn">
                <Plus size={15} /> Add Photo
              </button>
            </div>
            {(settings.galleryItems || []).length === 0 ? (
              <p className="text-white/40 text-sm font-light mt-6">No photos added yet — the site is showing placeholder photos. Add your team photos here to replace them.</p>
            ) : (
              <div className="space-y-3 mt-6" data-testid="gallery-items">
                {(settings.galleryItems || []).map((g, i) => (
                  <div
                    key={i}
                    draggable
                    onDragStart={() => setGalleryDrag(i)}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={() => { if (galleryDrag !== -1 && galleryDrag !== i) moveGalleryItem(galleryDrag, i); setGalleryDrag(-1); }}
                    className={`border rounded-lg p-3 flex gap-3 items-start transition-colors ${galleryDrag === i ? "border-gold bg-gold/5" : "border-white/10"}`}
                    data-testid={`gallery-item-${i}`}
                  >
                    <div className="flex items-center self-center text-white/30 cursor-grab active:cursor-grabbing" title="Drag to reorder" data-testid={`gallery-drag-${i}`}><GripVertical size={18} /></div>
                    <div className="w-24 h-16 rounded overflow-hidden bg-black/60 border border-white/10 flex items-center justify-center shrink-0">
                      {g.image ? <img src={g.image} alt="" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} /> : <ImageIcon size={18} className="text-white/25" />}
                    </div>
                    <div className="flex-1 space-y-2">
                      <Input value={g.caption || ""} onChange={(e) => updateGalleryItem(i, "caption", e.target.value)} placeholder="Caption (e.g. Team Huddle)" className="bg-background border-white/15 h-9 text-sm" data-testid={`gallery-caption-${i}`} />
                      <div className="flex items-center gap-2">
                        <Input value={g.image || ""} onChange={(e) => updateGalleryItem(i, "image", e.target.value)} placeholder="Image URL, or upload →" className="bg-background border-white/15 h-9 text-sm" data-testid={`gallery-image-${i}`} />
                        <label className="cursor-pointer inline-flex items-center gap-2 border border-white/25 text-white/70 cond-font uppercase tracking-widest text-xs px-3 py-2 rounded-sm hover:border-gold hover:text-gold transition-colors shrink-0" data-testid={`gallery-upload-${i}`}>
                          {galleryUploading === i ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
                          {galleryUploading === i ? "..." : "Upload"}
                          <input type="file" accept="image/*" className="hidden" disabled={galleryUploading === i} onChange={(e) => uploadGalleryImage(i, e.target.files?.[0])} />
                        </label>
                      </div>
                    </div>
                    <button onClick={() => removeGalleryItem(i)} className="text-white/40 hover:text-red-400 p-1 self-center" title="Remove photo" data-testid={`gallery-remove-${i}`}><Trash2 size={15} /></button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Latest News manager */}
          <div className="bg-card border border-white/10 rounded-xl p-7" data-testid="news-manager-card">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-md bg-gold/15 border border-gold/30 flex items-center justify-center text-gold"><Newspaper size={20} /></div>
                <div>
                  <h3 className="cond-font uppercase tracking-wide text-lg text-white leading-none">Latest News</h3>
                  <p className="text-white/40 text-xs font-light mt-1">Manage the "Latest news" cards — image, category, date, title and a short summary. Drag to reorder.</p>
                </div>
              </div>
              <button onClick={addNewsItem} className="inline-flex items-center gap-2 border border-white/25 text-white cond-font uppercase tracking-widest text-xs px-4 py-2.5 rounded-sm hover:border-gold hover:text-gold transition-colors" data-testid="news-add-btn">
                <Plus size={15} /> Add Article
              </button>
            </div>
            {(settings.newsItems || []).length === 0 ? (
              <p className="text-white/40 text-sm font-light mt-6">No articles added yet — the site is showing placeholder news. Add your own to replace them.</p>
            ) : (
              <div className="space-y-3 mt-6" data-testid="news-items">
                {(settings.newsItems || []).map((n, i) => (
                  <div
                    key={i}
                    draggable
                    onDragStart={() => setNewsDrag(i)}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={() => { if (newsDrag !== -1 && newsDrag !== i) moveNewsItem(newsDrag, i); setNewsDrag(-1); }}
                    className={`border rounded-lg p-3 flex gap-3 items-start transition-colors ${newsDrag === i ? "border-gold bg-gold/5" : "border-white/10"}`}
                    data-testid={`news-item-${i}`}
                  >
                    <div className="flex items-center self-center text-white/30 cursor-grab active:cursor-grabbing" title="Drag to reorder" data-testid={`news-drag-${i}`}><GripVertical size={18} /></div>
                    <div className="w-24 h-16 rounded overflow-hidden bg-black/60 border border-white/10 flex items-center justify-center shrink-0">
                      {n.image ? <img src={n.image} alt="" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} /> : <Newspaper size={18} className="text-white/25" />}
                    </div>
                    <div className="flex-1 space-y-2">
                      <div className="grid sm:grid-cols-2 gap-2">
                        <Input value={n.category || ""} onChange={(e) => updateNewsItem(i, "category", e.target.value)} placeholder="Category (e.g. Match Report)" className="bg-background border-white/15 h-9 text-sm" data-testid={`news-category-${i}`} />
                        <Input value={n.date || ""} onChange={(e) => updateNewsItem(i, "date", e.target.value)} placeholder="Date (e.g. July 12, 2025)" className="bg-background border-white/15 h-9 text-sm" data-testid={`news-date-${i}`} />
                      </div>
                      <Input value={n.title || ""} onChange={(e) => updateNewsItem(i, "title", e.target.value)} placeholder="Title" className="bg-background border-white/15 h-9 text-sm" data-testid={`news-title-${i}`} />
                      <textarea value={n.excerpt || ""} onChange={(e) => updateNewsItem(i, "excerpt", e.target.value)} placeholder="Short summary" rows={2} className="w-full bg-background border border-white/15 rounded-md px-3 py-2 text-sm text-white resize-y" data-testid={`news-excerpt-${i}`} />
                      <div className="flex items-center gap-2">
                        <Input value={n.image || ""} onChange={(e) => updateNewsItem(i, "image", e.target.value)} placeholder="Image URL, or upload →" className="bg-background border-white/15 h-9 text-sm" data-testid={`news-image-${i}`} />
                        <label className="cursor-pointer inline-flex items-center gap-2 border border-white/25 text-white/70 cond-font uppercase tracking-widest text-xs px-3 py-2 rounded-sm hover:border-gold hover:text-gold transition-colors shrink-0" data-testid={`news-upload-${i}`}>
                          {newsUploading === i ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
                          {newsUploading === i ? "..." : "Upload"}
                          <input type="file" accept="image/*" className="hidden" disabled={newsUploading === i} onChange={(e) => uploadNewsImage(i, e.target.files?.[0])} />
                        </label>
                      </div>
                    </div>
                    <button onClick={() => removeNewsItem(i)} className="text-white/40 hover:text-red-400 p-1 self-center" title="Remove article" data-testid={`news-remove-${i}`}><Trash2 size={15} /></button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Follow Us feed manager */}
          <div className="bg-card border border-white/10 rounded-xl p-7">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-md bg-gold/15 border border-gold/30 flex items-center justify-center text-gold"><Instagram size={20} /></div>
                <div>
                  <h3 className="cond-font uppercase tracking-wide text-lg text-white leading-none">Follow Us Feed</h3>
                  <p className="text-white/40 text-xs font-light mt-1">Add your latest posts — newest first on the site. Upload a photo + link it to the Instagram post.</p>
                </div>
              </div>
              <button onClick={addFeedItem} className="inline-flex items-center gap-2 border border-white/25 text-white cond-font uppercase tracking-widest text-xs px-4 py-2.5 rounded-sm hover:border-gold hover:text-gold transition-colors" data-testid="feed-add-btn">
                <Plus size={15} /> Add Post
              </button>
            </div>

            {/* Behold live-feed connection */}
            <div className="mt-6 border border-white/10 rounded-lg p-4 bg-background/40">
              <div className="flex items-center gap-2 mb-2">
                <Instagram size={15} className="text-gold" />
                <span className="cond-font uppercase tracking-widest text-xs text-gold">Auto Feed (Behold)</span>
              </div>
              <FieldA label="Behold Feed ID or URL">
                <Input value={settings.beholdFeedId || ""} onChange={(e) => setS("beholdFeedId", e.target.value)} placeholder="e.g. aB1cD2eF3g  (or https://feeds.behold.so/aB1cD2eF3g)" className="bg-background border-white/15" data-testid="behold-feed-id" />
              </FieldA>
              <p className="text-white/35 text-xs font-light mt-2">
                Paste your Behold feed ID and the site auto-updates with your newest @atletico_soflo posts. Leave blank to use the manual posts below.
              </p>
            </div>

            <div className="mt-4 flex items-center gap-3">
              <span className="cond-font uppercase tracking-widest text-xs text-white/60">Posts to show</span>
              <div className="w-28">
                <Select value={String(settings.feedCount || 6)} onValueChange={(v) => setS("feedCount", parseInt(v, 10))}>
                  <SelectTrigger className="bg-background border-white/15 h-9" data-testid="feed-count"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="6">6</SelectItem>
                    <SelectItem value="9">9</SelectItem>
                    <SelectItem value="12">12</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {(settings.followFeed || []).length === 0 ? (
              <p className="text-white/40 text-sm font-light mt-6">No manual posts — the site uses the Behold feed above if set, otherwise curated club photos.</p>
            ) : (
              <div className="grid sm:grid-cols-2 gap-4 mt-6" data-testid="feed-items">
                {(settings.followFeed || []).map((it, i) => (
                  <div
                    key={i}
                    draggable
                    onDragStart={() => setDragIndex(i)}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={() => { if (dragIndex !== -1 && dragIndex !== i) moveFeedItem(dragIndex, i); setDragIndex(-1); }}
                    className={`border rounded-lg p-4 flex gap-3 transition-colors ${dragIndex === i ? "border-gold bg-gold/5" : "border-white/10"}`}
                    data-testid={`feed-item-${i}`}
                  >
                    <div className="flex items-center text-white/30 cursor-grab active:cursor-grabbing" title="Drag to reorder" data-testid={`feed-drag-${i}`}>
                      <GripVertical size={18} />
                    </div>
                    <div className="shrink-0">
                      <div className="w-24 h-24 rounded-md overflow-hidden bg-background border border-white/10 flex items-center justify-center">
                        {it.image ? <img src={it.image} alt="" className="w-full h-full object-cover" /> : <Instagram size={22} className="text-white/25" />}
                      </div>
                      <label className="cursor-pointer mt-2 inline-flex items-center gap-1.5 text-xs cond-font uppercase tracking-widest text-white/60 hover:text-gold transition-colors" data-testid={`feed-upload-${i}`}>
                        {feedUploading === i ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
                        {feedUploading === i ? "..." : "Photo"}
                        <input type="file" accept="image/*" className="hidden" disabled={feedUploading === i} onChange={(e) => uploadFeedImage(i, e.target.files?.[0])} />
                      </label>
                    </div>
                    <div className="flex-1 space-y-2">
                      <Input value={it.caption} onChange={(e) => updateFeedItem(i, "caption", e.target.value)} placeholder="Caption (optional)" className="bg-background border-white/15 h-9 text-sm" data-testid={`feed-caption-${i}`} />
                      <Input value={it.link} onChange={(e) => updateFeedItem(i, "link", e.target.value)} placeholder="Instagram post link" className="bg-background border-white/15 h-9 text-sm" data-testid={`feed-link-${i}`} />
                      <div className="flex items-center justify-between">
                        <label className="flex items-center gap-2 text-xs cond-font uppercase tracking-widest text-white/60">
                          <Switch checked={it.isReel} onCheckedChange={(v) => updateFeedItem(i, "isReel", v)} className="data-[state=checked]:bg-gold" data-testid={`feed-reel-${i}`} /> Reel
                        </label>
                        <button onClick={() => removeFeedItem(i)} className="text-white/40 hover:text-red-400 p-1" data-testid={`feed-remove-${i}`}><Trash2 size={15} /></button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <p className="text-white/35 text-xs font-light mt-5">
              Drag posts by the handle to reorder them. With a Behold feed ID set, your live Instagram posts take over automatically.
            </p>
          </div>

          {isAdmin && (
            <div className="bg-card border border-white/10 rounded-xl p-7" data-testid="reminder-card">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-md bg-gold/15 border border-gold/30 flex items-center justify-center text-gold"><CalendarClock size={20} /></div>
                  <div>
                    <h3 className="cond-font uppercase tracking-wide text-lg text-white leading-none">Weekly "New Players" Reminder</h3>
                    <p className="text-white/40 text-xs font-light mt-1">Emails all staff a list of players still marked "New" (US Eastern time).</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="cond-font uppercase tracking-widest text-xs text-white/60">{settings.reminderEnabled !== false ? "On" : "Off"}</span>
                  <Switch checked={settings.reminderEnabled !== false} onCheckedChange={(v) => setS("reminderEnabled", v)} className="data-[state=checked]:bg-gold" data-testid="reminder-toggle" />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4 mt-6">
                <FieldA label="Day">
                  <Select value={settings.reminderDay || "mon"} onValueChange={(v) => setS("reminderDay", v)}>
                    <SelectTrigger className="bg-background border-white/15" data-testid="reminder-day"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {DAYS.map((d) => <SelectItem key={d.v} value={d.v}>{d.l}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </FieldA>
                <FieldA label="Time (ET)">
                  <Input
                    type="time"
                    value={`${String(settings.reminderHour ?? 9).padStart(2, "0")}:${String(settings.reminderMinute ?? 0).padStart(2, "0")}`}
                    onChange={(e) => {
                      const [h, m] = e.target.value.split(":").map((x) => parseInt(x, 10) || 0);
                      setSettings((s) => ({ ...s, reminderHour: h, reminderMinute: m }));
                    }}
                    className="bg-background border-white/15 text-white"
                    data-testid="reminder-time"
                  />
                </FieldA>
              </div>
              <button onClick={previewDigest} disabled={loadingDigest} className="mt-5 inline-flex items-center gap-2 border border-white/25 text-white cond-font uppercase tracking-widest text-xs px-4 py-2.5 rounded-sm hover:border-gold hover:text-gold transition-colors disabled:opacity-60" data-testid="digest-preview-btn">
                {loadingDigest ? <Loader2 size={15} className="animate-spin" /> : <Eye size={15} />} Preview Email
              </button>
            </div>
          )}

          <button
            onClick={saveSettings}
            disabled={saving}
            className="inline-flex items-center gap-2 bg-gold text-black cond-font uppercase tracking-widest font-semibold px-7 py-3.5 rounded-sm hover:bg-gold-light transition-colors disabled:opacity-60"
            data-testid="save-settings-btn"
          >
            <Save size={17} /> {saving ? "Saving..." : "Save Changes"}
          </button>
        </div>
      )}

      {showChangePw && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" data-testid="change-pw-modal" onClick={() => setShowChangePw(false)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={changePassword} className="w-full max-w-sm bg-card border border-white/10 rounded-xl p-7">
            <div className="flex items-center justify-between mb-5">
              <h3 className="display-font uppercase text-white text-lg">Change Password</h3>
              <button type="button" onClick={() => setShowChangePw(false)} className="text-white/50 hover:text-white" data-testid="change-pw-close"><X size={18} /></button>
            </div>
            <div className="space-y-3">
              <Input type="password" value={pwForm.current} onChange={(e) => setPwForm((p) => ({ ...p, current: e.target.value }))} placeholder="Current password" className="bg-background border-white/15" data-testid="pw-current" />
              <Input type="password" value={pwForm.next} onChange={(e) => setPwForm((p) => ({ ...p, next: e.target.value }))} placeholder="New password (min 6)" className="bg-background border-white/15" data-testid="pw-new" />
              <Input type="password" value={pwForm.confirm} onChange={(e) => setPwForm((p) => ({ ...p, confirm: e.target.value }))} placeholder="Confirm new password" className="bg-background border-white/15" data-testid="pw-confirm" />
            </div>
            <button type="submit" disabled={pwSaving} className="mt-5 w-full inline-flex items-center justify-center gap-2 bg-gold text-black cond-font uppercase tracking-widest text-sm font-semibold px-5 py-3 rounded-sm hover:bg-gold-light transition-colors disabled:opacity-60" data-testid="pw-submit">
              {pwSaving ? <Loader2 size={16} className="animate-spin" /> : <KeyRound size={16} />} Update Password
            </button>
          </form>
        </div>
      )}

      {digest && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" data-testid="digest-modal" onClick={() => setDigest(null)}>
          <div onClick={(e) => e.stopPropagation()} className="w-full max-w-lg bg-card border border-white/10 rounded-xl p-7 max-h-[85vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="display-font uppercase text-white text-lg leading-none">Weekly Digest Preview</h3>
                <p className="text-white/40 text-xs font-light mt-1">{digest.count} player(s) still marked New · sent to {digest.recipients?.length || 0} staff</p>
              </div>
              <button type="button" onClick={() => setDigest(null)} className="text-white/50 hover:text-white" data-testid="digest-close"><X size={18} /></button>
            </div>
            {digest.count === 0 ? (
              <p className="text-white/60 text-sm" data-testid="digest-empty">No players are currently marked "New", so no email would be sent this week.</p>
            ) : (
              <>
                <div className="rounded-lg overflow-hidden border border-white/10 bg-white mb-4">
                  <iframe title="digest-preview" srcDoc={digest.html} sandbox="" className="w-full h-72 bg-white" data-testid="digest-iframe" />
                </div>
                <p className="cond-font uppercase tracking-widest text-xs text-gold mb-2">Recipients</p>
                <p className="text-white/60 text-sm break-words" data-testid="digest-recipients">{(digest.recipients || []).join(", ")}</p>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function FieldA({ label, children }) {
  return (
    <div>
      <Label className="cond-font uppercase tracking-wide text-xs text-white/60 mb-2 block">{label}</Label>
      {children}
    </div>
  );
}

function VideoSectionEditor({ title, type, source, loops, logoBetween, uploading, onType, onSource, onLoops, onLogo, onUpload, onUploadImage, onUseDefault }) {
  const isYouTube = type === "youtube";
  const isImage = type === "image";
  const updateLoop = (i, key, clock) => {
    const next = loops.map((l, idx) => (idx === i ? { ...l, [key]: clockToSec(clock) } : l));
    onLoops(next);
  };
  const addLoop = () => onLoops([...(loops || []), { start: 0, end: 0 }]);
  const removeLoop = (i) => onLoops(loops.filter((_, idx) => idx !== i));

  const label = isYouTube ? "YouTube URL or ID" : isImage ? "Image URL" : "MP4 URL";

  return (
    <div className="space-y-4">
      <p className="cond-font uppercase tracking-widest text-xs text-gold">{title}</p>
      <FieldA label="Source Type">
        <Select value={type} onValueChange={onType}>
          <SelectTrigger className="bg-background border-white/15" data-testid={`vid-type-${title}`}><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="youtube">YouTube</SelectItem>
            <SelectItem value="mp4">MP4 Video (link or upload)</SelectItem>
            <SelectItem value="image">Image (link or upload)</SelectItem>
          </SelectContent>
        </Select>
      </FieldA>

      <FieldA label={label}>
        <Input value={source || ""} onChange={(e) => onSource(e.target.value)} className="bg-background border-white/15" data-testid={`vid-src-${title}`} />
      </FieldA>

      {isImage && (
        <div className="flex flex-wrap items-center gap-3">
          <div className="w-28 h-20 rounded-md overflow-hidden bg-background border border-white/10 flex items-center justify-center shrink-0">
            {source ? <img src={source} alt="" className="w-full h-full object-cover" /> : <ImageIcon size={20} className="text-white/25" />}
          </div>
          <label className="cursor-pointer inline-flex items-center gap-2 border border-white/25 text-white cond-font uppercase tracking-widest text-xs px-4 py-2.5 rounded-sm hover:border-gold hover:text-gold transition-colors" data-testid={`img-upload-${title}`}>
            {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
            {uploading ? "Uploading..." : "Upload Image"}
            <input type="file" accept="image/*" className="hidden" disabled={uploading} onChange={(e) => onUploadImage(e.target.files?.[0])} />
          </label>
          <button type="button" onClick={onUseDefault} className="inline-flex items-center gap-2 border border-white/25 text-white/70 cond-font uppercase tracking-widest text-xs px-4 py-2.5 rounded-sm hover:border-gold hover:text-gold transition-colors" data-testid={`img-default-${title}`}>
            Use Default Photo
          </button>
        </div>
      )}

      {type === "mp4" && (
        <div className="space-y-3">
          {source && (
            <video
              key={source}
              src={source}
              controls
              muted
              playsInline
              className="w-full max-w-sm rounded-md border border-white/10 bg-black"
              data-testid={`vid-preview-${title}`}
            />
          )}
          <label className="cursor-pointer inline-flex items-center gap-2 border border-white/25 text-white cond-font uppercase tracking-widest text-xs px-4 py-2.5 rounded-sm hover:border-gold hover:text-gold transition-colors" data-testid={`vid-upload-${title}`}>
            {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
            {uploading ? "Uploading..." : source ? "Replace video" : "Upload a video"}
            <input type="file" accept="video/*" className="hidden" disabled={uploading} onChange={(e) => onUpload(e.target.files?.[0])} />
          </label>
          {source && <p className="text-white/40 text-[11px] font-light">Video set. Click <span className="text-gold">Save Changes</span> at the bottom to publish it.</p>}
        </div>
      )}

      {isYouTube && (
        <div className="border border-white/10 rounded-md p-4 space-y-3">
          <div className="flex items-center justify-between">
            <span className="cond-font uppercase tracking-widest text-[11px] text-white/60">Loop Windows</span>
            <button onClick={addLoop} className="inline-flex items-center gap-1 text-gold text-xs cond-font uppercase tracking-widest hover:text-gold-light" data-testid={`loop-add-${title}`}>
              <Plus size={13} /> Add
            </button>
          </div>
          {(loops || []).length === 0 && <p className="text-white/40 text-xs font-light">No loops — the full video plays on repeat.</p>}
          {(loops || []).map((l, i) => (
            <div key={i} className="flex items-center gap-2">
              <Input defaultValue={secToClock(l.start)} onBlur={(e) => updateLoop(i, "start", e.target.value)} placeholder="mm:ss" className="bg-background border-white/15 h-9 text-sm" data-testid={`loop-start-${title}-${i}`} />
              <span className="text-white/40 text-xs">→</span>
              <Input defaultValue={secToClock(l.end)} onBlur={(e) => updateLoop(i, "end", e.target.value)} placeholder="mm:ss" className="bg-background border-white/15 h-9 text-sm" data-testid={`loop-end-${title}-${i}`} />
              <button onClick={() => removeLoop(i)} className="text-white/40 hover:text-red-400 p-1" data-testid={`loop-remove-${title}-${i}`}><Trash2 size={15} /></button>
            </div>
          ))}
          <div className="flex items-center justify-between pt-1">
            <span className="cond-font uppercase tracking-widest text-[11px] text-white/60">Twirl logo between loops</span>
            <Switch checked={logoBetween} onCheckedChange={onLogo} className="data-[state=checked]:bg-gold" data-testid={`loop-logo-${title}`} />
          </div>
        </div>
      )}
    </div>
  );
}
